import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetRestaurantIdentityQuery } from '@/app/api/hubApi';
import { useGetConfigurationQuery } from '@/app/api/resourceApi';
import { isTransportError } from '@/app/api/loadResilience';
import ThemeProvider from '@/features/restaurant/ThemeProvider';
import NotFoundPage from '@/pages/NotFoundPage';
import ConnectionLostNotice from '@/components/chrome/ConnectionLostNotice';
import GlobalLoader from '@/components/ui/GlobalLoader';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { cancelNameChange, rehydrate, selectWaiterAuth } from './waiterSlice';
import { hydrateWaiterCart, sweepWaiterCartRecovery, waiterCartSessionOfRecoveryKey } from './waiterCartSlice';
import { registerWaiterApp } from './installApp/registerWaiterApp';
import { installErrorRingBuffer } from './bugReport/bugReportCore';
import { useIdleLock } from './useIdleLock';
import { useWaiterTokenRenewal } from './useWaiterTokenRenewal';
import PinPad from './PinPad';
import NamePicker from './NamePicker';
import NotesListPage from './NotesListPage';
import TicketPage from '@/features/waiterTicket/TicketPage';
import type { WaiterToastAction } from '@/features/waiterTicket/waiterToast';

export default function WaiterShell() {
  const { restaurantUuid = '' } = useParams<{ restaurantUuid: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    data: identity,
    isLoading,
    error,
    refetch,
  } = useGetRestaurantIdentityQuery(restaurantUuid, {
    skip: !restaurantUuid,
  });
  // SC-1023 — le carnet lit aussi la configuration (resource-api : vues,
  // tournée, livraison). Quand c'est elle qui cale, rien ne le disait : le
  // carnet restait affiché, à moitié vide. Seule une panne de TRANSPORT
  // bloque ici ; une réponse du serveur (403 licence…) garde le comportement
  // d'avant, chaque vue décidant pour elle-même.
  const {
    isLoading: configLoading,
    error: configError,
    refetch: refetchConfig,
  } = useGetConfigurationQuery(restaurantUuid, { skip: !restaurantUuid });
  const auth = useAppSelector(selectWaiterAuth);
  // Carries the just-validated PIN from PinPad to NamePicker for the
  // re-unlock-with-name call (spec §2.3: name switch = re-PIN). Never
  // persisted — lost on reload, which just sends the waiter back to PinPad.
  const [pendingPin, setPendingPin] = useState<string | null>(null);

  // Spec §2.8 — the ticket's toast (`ticket-toast`) is owned HERE, one level
  // above the nested `<Routes>`, not by `TicketPage` itself: a toast that
  // ALSO navigates away (e.g. « Commande introuvable ») would otherwise
  // unmount together with the page that raised it, in the same React commit,
  // and never actually paint. Living beside `<Routes>` it survives the swap
  // to `NotesListPage` (or back to the PIN pad).
  const [toast, setToast] = useState<{ message: string; action?: WaiterToastAction } | null>(null);
  const showToast = useCallback((message: string, action?: WaiterToastAction) => {
    setToast({ message, action });
  }, []);
  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(id);
  }, [toast]);

  useEffect(() => {
    if (restaurantUuid) dispatch(rehydrate(restaurantUuid));
  }, [dispatch, restaurantUuid]);

  // DF-0383 — un autre onglet de cet appareil vient de reprendre des plats du
  // panier de celui-ci (« Récupérer » sur son ticket) : ils sont à lui, ce
  // panier les lâche tout de suite, quel que soit l'écran ouvert ici — sinon
  // les deux onglets pourraient envoyer la même tournée.
  useEffect(() => {
    sweepWaiterCartRecovery();
    const onStorage = (event: StorageEvent) => {
      const sessionUuid = waiterCartSessionOfRecoveryKey(event.key);
      if (sessionUuid) dispatch(hydrateWaiterCart(sessionUuid));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [dispatch]);

  // SC-1082 — le ring buffer d'erreurs client de « Signaler un bug » : posé
  // une seule fois ici, la racine commune à tout l'app serveur, pour que le
  // rapport parte avec ce qui a précédé la remontée quelle que soit l'écran
  // ouvert au moment du bug (idempotent — cf. bugReportCore.ts).
  useEffect(() => {
    installErrorRingBuffer();
  }, []);

  // SC-906 — le manifeste et le service worker qui rendent le carnet
  // installable sont poses ici, la seule racine qui ne se monte que sur la
  // surface du personnel : ils designent un restaurant precis et n'ont rien a
  // faire dans l'onglet d'un convive.
  // Le nom arrive avec la fiche du restaurant, une requete plus tard : le
  // manifeste est donc pose deux fois, generique puis nomme. C'est voulu — le
  // worker, lui, n'attend pas, et une icone sans nom vaut mieux qu'une absence
  // d'installation si la fiche ne repond pas.
  useEffect(() => {
    if (restaurantUuid) registerWaiterApp(restaurantUuid, identity?.name);
  }, [restaurantUuid, identity?.name]);

  // Spec §3.1: idle_lock_seconds ⇒ uiLocked ⇒ back to PinPad. Only armed once
  // a waiter is actually unlocked — locking an already-locked screen is a no-op.
  useIdleLock(auth?.idleLockSeconds, !!auth && !auth.uiLocked);

  // SC-799 — l'autre moitie : le verrouillage d'inactivite ci-dessus ne dit
  // rien de l'echeance du jeton, qui elle expirait a 12 h fixes et renvoyait
  // au pave PIN chaque matin. Renouvelle en fond tant que l'appareil sert.
  useWaiterTokenRenewal(auth);

  if (!restaurantUuid) return <NotFoundPage />;
  if (isLoading || configLoading) return <GlobalLoader />;
  // DF-0257 — le réseau qui cale n'est pas un mauvais lien : la fiche du
  // restaurant n'a simplement jamais répondu. Le renvoyer sur « restaurant
  // introuvable » enverrait le serveur chercher un problème qui n'existe pas.
  if (isTransportError(error) || isTransportError(configError)) {
    return (
      <ConnectionLostNotice
        onRetry={() => {
          if (error) refetch();
          if (configError) refetchConfig();
        }}
      />
    );
  }
  if (error || !identity) return <NotFoundPage />;

  return (
    <ThemeProvider theme={identity.theme}>
      {!auth || auth.uiLocked ? (
        <PinPad
          restaurantUuid={restaurantUuid}
          restaurantName={identity.name}
          restaurantLogoUrl={identity.logoUrl}
          restaurantLogoInverseUrl={identity.logoInverseUrl}
          // SC-761 — a switch picked in the notes-list identity sheet is
          // bound by THIS unlock (the pending name wins over the current
          // one), so the waiter never crosses the name picker again.
          knownWaiterName={auth?.pendingWaiterName || auth?.waiterName}
          switchingTo={auth?.pendingWaiterName ?? null}
          onCancelSwitch={() => dispatch(cancelNameChange())}
          onUnlocked={(pin) => {
            setPendingPin(pin);
            // Spec §1.7 re-unlock: a lock (manual 🔒 or idle timeout) that
            // fired while the waiter was serving a table OR a ticket returns
            // them there instead of the bare notes list. `auth` here is the
            // PRE-unlock snapshot captured by this closure — setWaiterAuth
            // (dispatched by PinPad just before calling onUnlocked) always
            // drops lockedFrom on the fresh auth object, so this read only
            // ever fires once per lock.
            if (auth?.lockedFrom?.kind === 'table') {
              navigate(`/${restaurantUuid}/table/${auth.lockedFrom.tableToken}`);
            } else if (auth?.lockedFrom?.kind === 'ticket') {
              navigate(`/${restaurantUuid}/serveur/ticket/${auth.lockedFrom.sessionUuid}`);
            }
          }}
        />
      ) : !auth.waiterName ? (
        <NamePicker restaurantUuid={restaurantUuid} waiterNames={auth.waiterNames} pin={pendingPin} />
      ) : (
        // D1 (05-regression-risks.md) — the ticket screen is rendered HERE,
        // as a child of this same PIN gate / idle lock / ThemeProvider, not as
        // a sibling route in App.tsx: a sibling would bypass all three.
        <Routes>
          <Route
            index
            element={
              <NotesListPage
                restaurantUuid={restaurantUuid}
                restaurantName={identity.name}
                restaurantLogoUrl={identity.logoUrl}
                restaurantLogoInverseUrl={identity.logoInverseUrl}
                token={auth.token}
                waiterName={auth.waiterName}
                waiterNames={auth.waiterNames}
                showToast={showToast}
              />
            }
          />
          <Route
            path="ticket/:sessionUuid"
            element={<TicketPage restaurantUuid={restaurantUuid} token={auth.token} showToast={showToast} />}
          />
        </Routes>
      )}
      {toast && (
        <div
          data-testid="ticket-toast"
          role="status"
          className="fixed inset-x-3 z-[1300] bg-ink text-paper rounded-xl p-3 font-mono text-base flex items-center justify-between gap-3"
          style={{ bottom: 'calc(64px + env(safe-area-inset-bottom) + 12px)' }}
        >
          <span>{toast.message}</span>
          {toast.action && (
            <button
              type="button"
              onClick={() => {
                toast.action?.onClick();
                setToast(null);
              }}
              className="shrink-0 underline font-display"
            >
              {toast.action.label}
            </button>
          )}
          <button
            type="button"
            onClick={() => setToast(null)}
            aria-label={t('cart.close')}
            className="shrink-0 font-display"
          >
            ×
          </button>
        </div>
      )}
    </ThemeProvider>
  );
}
